import { DatePicker, Form, Input, Modal, Select, Spin, Radio } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import cn from 'classnames';
import calendarIcon from '../../../../../resource/forms/Calendar.svg';
import listIcon from '../../../../../resource/forms/ListRectangular.svg';
import styles from '../modalStyles.module.scss';
import meterStyles from './MeterModal.module.scss';
import transformMeterData from '../../../utils/transformMeterData';
import moment from 'moment';
import editMeter from '../../../requests/editMeter';
import createMeter from '../../../requests/createMeter';
import useMeterTypes from '../../../queries/useMeterTypes';
import useFacilityTariffs, {
  getFacilityTariffs,
} from '../../../queries/useFacilityTariffs';
import queries from '../../../queries/queries';
import useMeterTariffTypes from '../../../queries/useMeterTariffTypes';
import createMeterReadings from '../../../requests/createMeterReadings';
import NoTariffWarning from './components/NoTariffWarning/NoTariffWarning';
import { meterReadingInputs } from './constants';
import { getMeterReadings, getOptions } from './utils';
import { getMeter } from '../../../requests/getMeter';
import { dateFormatList } from '../../../constants/dateFormatList';

const MeterModalNode = ({
  form,
  facilityData,
  officeData,
  visible,
  onClose,
  initialValues = null,
  onOk,
}) => {
  const { validateFieldsAndScroll, getFieldDecorator, resetFields } = form;

  const queryClient = useQueryClient();

  const [meterTypeId, setMeterTypeId] = useState(null);
  const [meterTypeName, setMeterTypeName] = useState('');
  const [availableMeterTypes, setAvailableMeterTypes] = useState([]);

  const [facilityTariff, setFacilityTariff] = useState('');

  const [initialFacilityTariffs, setInitialFacilityTariffs] = useState([]);

  const [meterTariffType, setMeterTariffType] = useState('');

  const [readingFields, setReadingFields] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const { data: meterTypes } = useMeterTypes();

  const { data: facilityTariffs, isFetching: isTariffsFetching } =
    useFacilityTariffs(facilityData.id, meterTypeId, !!meterTypeId);

  const { data: meterTariffTypes } = useMeterTariffTypes();

  const handleMeterTariffTypeChange = (e) => {
    setMeterTariffType(e.target.value);
  };

  const getInitialFacilityTariffs = useCallback(async () => {
    const initialFacilityTariffs = await getFacilityTariffs(facilityData.id);
    setInitialFacilityTariffs(initialFacilityTariffs);
  }, [facilityData]);

  useEffect(() => {
    getInitialFacilityTariffs();
  }, [getInitialFacilityTariffs]);

  useEffect(() => {
    if (!initialValues) {
      setMeterTariffType(JSON.stringify(meterTariffTypes[0]));
    } else {
      if (initialValues.tariffs?.length === 1) {
        setMeterTariffType(JSON.stringify(meterTariffTypes[0]));
      }

      if (initialValues.tariffs?.length === 2) {
        setMeterTariffType(JSON.stringify(meterTariffTypes[1]));
      }
    }
  }, [meterTariffTypes, initialValues]);

  useEffect(() => {
    if (meterTariffType) {
      const tariffType = JSON.parse(meterTariffType);
      const name = tariffType.name;

      const fields = meterReadingInputs.filter((input) => {
        if (name === 'Однотарифный') {
          return input.name === 'generalReading';
        }

        if (name === 'Двухтарифный') {
          return input.name === 'dayReading' || input.name === 'nightReading';
        }

        return input;
      });

      setReadingFields(fields);
    }
  }, [meterTariffType]);

  const handleChangeMeterType = (e) => {
    setMeterTypeId(e.key);
    setMeterTypeName(e.label);

    queryClient.invalidateQueries(queries.facilityTariffs);
  };

  const closeModal = () => {
    resetFields();
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    validateFieldsAndScroll(async (err, values) => {
      if (!err) {
        const { generalReading, dayReading, nightReading } = values;

        if (values.meterTariffType) {
          values.meterTariffType = JSON.parse(values.meterTariffType);
        }

        if (initialValues) {
          values.id = initialValues.id;
        }

        const transformedData = transformMeterData(
          values,
          facilityData.id,
          officeData?.id
        );

        setIsLoading(true);
        const response = initialValues
          ? await editMeter(transformedData)
          : await createMeter(transformedData);

        if (response?.result === 'Ok') {
          if (facilityTariffs.length && generalReading) {
            const data = {
              generalReading,
            };

            const meterReadings = getMeterReadings(data, facilityTariffs);
            await createMeterReadings(response.id, { meterReadings });
          }

          if (dayReading || nightReading) {
            const meter = await getMeter(response.id);

            if (meter && Object.keys(meter).length) {
              const meterReadings = meter.tariffs.map((tariff) => {
                let value;

                meterReadingInputs.forEach((input) => {
                  const { title, name } = input;

                  if (tariff.name.includes(title) && values[name]) {
                    value = values[name];
                  }
                });

                return {
                  tariff: { id: tariff.id },
                  value,
                  tariffValue: meter.rentalHouseTariff?.value,
                };
              });

              if (meterReadings.length) {
                await createMeterReadings(response.id, { meterReadings });
              }
            }
          }

          onOk();
        }

        setIsLoading(false);
        closeModal();
      }
    });
  };

  const select = useMemo(
    () => (
      <Select
        onChange={handleChangeMeterType}
        labelInValue
        placeholder="Выбрать из списка"
      >
        {availableMeterTypes.map((type) => (
          <Select.Option key={type.id} value={type.id}>
            {type.name}
          </Select.Option>
        ))}
      </Select>
    ),
    [availableMeterTypes]
  );

  const facilityAddrAndName = `${facilityData.house?.fullAddress}, ${facilityData.name}`;

  const address = officeData
    ? `${facilityAddrAndName}, ${officeData.name}`
    : `${facilityAddrAndName}`;

  useEffect(() => {
    if (initialValues) {
      setMeterTypeId(initialValues.type.id);
      setMeterTypeName(initialValues.type.name);

      queryClient.invalidateQueries(queries.facilityTariffs);
    }
  }, [initialValues]);

  useEffect(() => {
    if (meterTypeName === '') {
      setFacilityTariff('');
    }

    if (meterTypeName !== '' && facilityTariffs?.length) {
      const tariff = facilityTariffs[0].value;
      setFacilityTariff(tariff);
    }
  }, [meterTypeName, facilityTariffs]);

  useEffect(() => {
    if (initialFacilityTariffs.length) {
      const availableMeterTypes = meterTypes.reduce((acc, meterType) => {
        meterType.tariffs.forEach((meterTypeTariff) => {
          const availableMeterType = initialFacilityTariffs.find(
            (facilityTariff) => facilityTariff.tariff.id === meterTypeTariff.id
          );

          if (availableMeterType) {
            const isExists = acc.find((item) => item.id === meterType.id);

            if (!isExists) {
              acc.push(meterType);
            }
          }
        });

        return acc;
      }, []);

      setAvailableMeterTypes(availableMeterTypes);
    }
  }, [meterTypes, initialFacilityTariffs]);

  return (
    <Modal
      title={`${initialValues ? 'Редактировать' : 'Добавить'} счётчик`}
      visible={visible}
      onOk={handleSubmit}
      onCancel={closeModal}
      width={650}
      okText={`${initialValues ? 'Обновить' : 'Создать'} счетчик`}
      cancelButtonProps={{ disabled: isLoading }}
      okButtonProps={{ disabled: isLoading || !initialFacilityTariffs.length }}
      confirmLoading={isLoading}
    >
      <Spin spinning={isLoading || isTariffsFetching}>
        {!initialValues &&
          meterTypes &&
          availableMeterTypes.length !== meterTypes.length && (
            <NoTariffWarning
              onClick={closeModal}
              facilityAddress={facilityData.house.fullAddress}
              facilityId={facilityData.id}
              availableMeterTypes={availableMeterTypes}
            />
          )}
        <Form layout="vertical" onSubmit={handleSubmit}>
          <div className={meterStyles.address}>
            {address}
            <img className={meterStyles.addressIcon} src={listIcon} alt={''} />
          </div>
          <div className={styles.flex}>
            <Form.Item label="Номер" className={styles.flexHalf}>
              {getFieldDecorator('meterCode', {
                initialValue: initialValues && initialValues.meterCode,
                ...getOptions('Введите номер.'),
              })(<Input />)}
            </Form.Item>
            <Form.Item label="Тип" className={styles.flexFull}>
              {getFieldDecorator('type', {
                initialValue: initialValues
                  ? {
                      key: initialValues.type?.id,
                      label: initialValues.type?.name,
                    }
                  : undefined,
                ...getOptions('Выберите тип.'),
              })(select)}
            </Form.Item>
          </div>

          <div className={styles.flex}>
            <Form.Item
              label="Дата ввода в эксплуатацию"
              className={styles.flexFull}
            >
              {getFieldDecorator('commissioningDate', {
                initialValue:
                  initialValues && initialValues.commissioningDate
                    ? moment(initialValues.commissioningDate, moment.ISO_8601)
                    : null,
              })(
                <DatePicker
                  className={styles.datePicker}
                  suffixIcon={<img src={calendarIcon} />}
                  placeholder="Выбрать дату"
                  format={dateFormatList}
                />
              )}
            </Form.Item>
            <Form.Item label="Дата поверки" className={styles.flexFull}>
              {getFieldDecorator('verificationDate', {
                initialValue:
                  initialValues && initialValues.verificationDate
                    ? moment(initialValues.verificationDate, moment.ISO_8601)
                    : null,
              })(
                <DatePicker
                  className={styles.datePicker}
                  suffixIcon={<img src={calendarIcon} />}
                  placeholder="Выбрать дату"
                  format={dateFormatList}
                />
              )}
            </Form.Item>
          </div>

          <div className={styles.flex}>
            <Form.Item label="Коэффициент" className={styles.flexFull}>
              {getFieldDecorator('coefficient', {
                initialValue: initialValues && initialValues.coefficient,
                ...getOptions('Укажите коэффициент'),
              })(<Input type="number" />)}
            </Form.Item>
            <Form.Item
              label="Тариф объекта на указанный тип счетчика"
              className={cn(styles.flexFull, meterStyles.readOnlyField)}
            >
              {getFieldDecorator('facilityTariff', {
                initialValue: facilityTariff,
              })(<Input readOnly />)}
            </Form.Item>
            {officeData && (
              <Form.Item
                label="Тариф помещения на указанный тип счетчика"
                className={cn(styles.flexFull, meterStyles.readOnlyField)}
              >
                {getFieldDecorator('roomTariff', {
                  initialValue: facilityTariff,
                })(<Input readOnly />)}
              </Form.Item>
            )}
          </div>
          {officeData &&
            meterTypeName?.trim() === 'Электричество' &&
            meterTariffType && (
              <>
                <div className={styles.flex} style={{ display: 'none' }}>
                  <Form.Item label="Вид тарифа" className={styles.flexFull}>
                    {getFieldDecorator('meterTariffType', {
                      initialValue: meterTariffType,
                    })(
                      <div className={meterStyles.radioWrapper}>
                        {meterTariffTypes?.map((type) => (
                          <label
                            key={type.id}
                            htmlFor={type.id}
                            className={meterStyles.label}
                          >
                            <input
                              id={type.id}
                              className={meterStyles.invisibleRadio}
                              type="radio"
                              value={JSON.stringify(type)}
                              checked={
                                JSON.parse(meterTariffType).id === type.id
                              }
                              onChange={handleMeterTariffTypeChange}
                            />
                            <span className={meterStyles.visibleRadio} />
                            <span className={meterStyles.labelText}>
                              {type.name}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </Form.Item>
                </div>
                {!initialValues && (
                  <div className={styles.flex}>
                    {readingFields.map((field) => {
                      return (
                        <Form.Item
                          key={field.id}
                          label={`Начальные показания (${field.title})`}
                        >
                          {getFieldDecorator(field.name)(
                            <Input type="number" />
                          )}
                        </Form.Item>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          {officeData &&
            meterTypeName?.trim() !== 'Электричество' &&
            !initialValues && (
              <div className={styles.flex}>
                <Form.Item label="Начальные показания счетчика">
                  {getFieldDecorator('generalReading')(<Input type="number" />)}
                </Form.Item>
              </div>
            )}
          <Form.Item label="Комментарий" className={styles.flexFull}>
            {getFieldDecorator('description', {
              initialValue: initialValues?.description,
            })(
              <TextArea rows={3} placeholder={'Ваш комментарий'} />
            )}
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

const MeterModal = Form.create({ name: 'meter_modal' })(MeterModalNode);

export default MeterModal;
